import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, Shield, ExternalLink } from 'lucide-react';

const CookieConsentModal = ({ isOpen, onAccept, onDeny }) => {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
        style={{ margin: 0 }}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{ type: "spring", duration: 0.5 }}
          className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-brand to-brand-hover p-6 rounded-t-2xl">
            <div className="flex items-center justify-center mb-2">
              <div className="bg-white/20 p-3 rounded-full">
                <Cookie className="h-8 w-8 text-brand-dark" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-brand-dark text-center">
              Cookie Consent
            </h2>
          </div>

          {/* Content */}
          <div className="p-6 space-y-4">
            <div className="flex items-start space-x-3 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <Shield className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-gray-700 leading-relaxed">
                We value your privacy and are committed to protecting your personal information.
              </p>
            </div>

            <div className="space-y-3">
              <h3 className="font-semibold text-gray-900 text-lg">How We Use Cookies</h3>
              
              <div className="space-y-2 text-sm text-gray-700">
                <p>
                  This portal uses cookies to enhance your browsing experience and provide 
                  personalized features. Cookies help us:
                </p>
                
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Keep you securely logged in to your account</li>
                  <li>Remember your preferences and settings</li>
                  <li>Maintain your shopping cart items</li>
                  <li>Analyze site usage to improve our services</li>
                  <li>Provide personalized content and features</li>
                </ul>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mt-4">
                <h4 className="font-semibold text-gray-900 mb-2 text-sm">Types of Cookies Used:</h4>
                <div className="space-y-2 text-xs text-gray-600">
                  <div>
                    <span className="font-medium">Essential Cookies:</span> Required for basic 
                    site functionality (authentication, security)
                  </div>
                  <div>
                    <span className="font-medium">Functional Cookies:</span> Remember your 
                    preferences and settings
                  </div>
                  <div>
                    <span className="font-medium">Analytics Cookies:</span> Help us understand 
                    how you use the portal
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-3 mt-4 text-xs">
                <a
                  href="https://miningandhydraulics.com.au/privacy-policy/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center text-blue-600 hover:text-blue-800 transition-colors"
                >
                  <ExternalLink className="h-3 w-3 mr-1" />
                  Privacy Policy
                </a>
                <a
                  href="https://miningandhydraulics.com.au/terms-conditions/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center text-blue-600 hover:text-blue-800 transition-colors"
                >
                  <ExternalLink className="h-3 w-3 mr-1" />
                  Terms & Conditions
                </a>
              </div>
            </div>
          </div>

          {/* Footer with Action Buttons */}
          <div className="bg-gray-50 px-6 py-4 rounded-b-2xl border-t border-gray-200">
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={onAccept}
                className="flex-1 bg-gradient-to-r from-brand to-brand-hover text-brand-dark px-6 py-3 rounded-lg font-semibold hover:from-brand-hover hover:to-brand transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-[1.02]"
              >
                Accept All Cookies
              </button>
              <button
                onClick={onDeny}
                className="flex-1 bg-white border-2 border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 shadow-sm hover:shadow"
              >
                Deny Optional Cookies
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-3 text-center">
              Essential cookies required for site functionality will still be used regardless of your choice.
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CookieConsentModal;
